import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Info, ExternalLink } from "lucide-react";

interface Link {
  label: string;
  url: string;
  audience?: string;
  type?: string;
}

interface ClinicalReferencesCollapsibleProps {
  links: Link[];
  isDarkMode?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  heading?: string;
  className?: string;
}

export function ClinicalReferencesCollapsible({
  links,
  isDarkMode = false,
  isOpen = false,
  onOpenChange,
  heading,
  className,
}: ClinicalReferencesCollapsibleProps) {
  const [internalOpen, setInternalOpen] = useState(isOpen);
  const open = onOpenChange !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  if (links.length === 0) {
    return null;
  }

  const defaultHeading = `Clinical References (${links.length})`;
  const displayHeading = heading || defaultHeading;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={`mt-1.5 ${className || ""}`}
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          title={
            open ? "Collapse clinical references" : "Expand clinical references"
          }
          className="text-xs h-auto py-1"
        >
          <Info className="h-3 w-3" />
          <span>{displayHeading}</span>
          <ChevronDown
            className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="flex flex-wrap gap-1.5">
          {links.map((link, idx) => (
            <a
              key={idx}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${link.label} in a new tab`}
              style={{
                animation: open
                  ? `fadeInSlideUp 300ms ease-out ${idx * 10}ms both`
                  : "none",
              }}
              className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                isDarkMode
                  ? "border-gray-700 bg-gray-900 text-blue-300 hover:bg-gray-800 hover:border-blue-600"
                  : "border-gray-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-300"
              }`}
            >
              <Info
                className={`h-3 w-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
              <span className="underline underline-offset-2 decoration-dotted">
                {link.label}
              </span>
              <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
            </a>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
